import React, { useState, useEffect, useCallback } from 'react';
import { stationsService, sitesService, ocppLogsService, locationsService, getErrorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  NativeButton,
  NativeCard,
  NativeCardHeader,
  NativeCardTitle,
  NativeCardContent,
  NativeInput,
  NativeSelect,
  NativeBadge,
  NativeAlert,
  NativeModal,
  NativeTable,
  NativeTableHeader,
  NativeTableHead,
  NativeTableBody,
  NativeTableRow,
  NativeTableCell,
  EmptyState
} from '../components/NativeComponents';
import { 
  Loader2, 
  Plus, 
  Pencil,
  Trash2,
  Search,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  Zap,
  Play,
  Square,
  RotateCw,
  Unlock,
  Wifi,
  WifiOff,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';

const STATION_STATUSES = {
  available: { label: 'Müsait', variant: 'success' },
  Available: { label: 'Müsait', variant: 'success' },
  charging: { label: 'Şarj Ediyor', variant: 'info' },
  Charging: { label: 'Şarj Ediyor', variant: 'info' },
  preparing: { label: 'Hazırlanıyor', variant: 'warning' },
  Preparing: { label: 'Hazırlanıyor', variant: 'warning' },
  offline: { label: 'Çevrimdışı', variant: 'secondary' },
  Unavailable: { label: 'Çevrimdışı', variant: 'secondary' },
  faulted: { label: 'Arızalı', variant: 'danger' },
  Faulted: { label: 'Arızalı', variant: 'danger' },
  SuspendedEV: { label: 'Askıda (EV)', variant: 'warning' },
  SuspendedEVSE: { label: 'Askıda (EVSE)', variant: 'warning' },
  Finishing: { label: 'Tamamlanıyor', variant: 'info' }
};

const CONNECTOR_TYPES = ['Type 2', 'Type 2 AC', 'Type 2 DC', 'CCS2', 'CHAdeMO', 'Tesla'];

export default function StationsPage() {
  const { user, isSuperAdmin, isHesapYoneticisi, getEffectiveRole, getEffectiveSiteId } = useAuth();
  
  // Admin ve Super Admin dışındaki roller sadece kendi sitelerini görebilir
  const effectiveRole = getEffectiveRole();
  const isEffectiveHesapYoneticisi = effectiveRole === 'hesap_yoneticisi' || effectiveRole === 'site_manager';
  const canSelectSite = effectiveRole === 'super_admin' || effectiveRole === 'admin';
  
  // Simülasyon modunda simulatedSiteId kullan, yoksa user.site_id
  const effectiveSiteId = getEffectiveSiteId ? getEffectiveSiteId() : user?.site_id;
  
  const [stations, setStations] = useState([]);
  const [sites, setSites] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal state
  const [modal, setModal] = useState({
    isOpen: false,
    mode: 'create',
    stationId: null,
    saving: false
  });
  
  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    station: null,
    deleting: false
  });

  // Remote control modal state
  const [controlModal, setControlModal] = useState({
    isOpen: false,
    station: null,
    action: null,
    executing: false
  });
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    site_id: '',
    location_id: '',
    ocpp_id: '',
    qr_code: '',
    serial_number: '',
    connector_type: 'Type 2',
    power_capacity: 22,
    status: 'available'
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      let [stationsData, sitesData, locationsData] = await Promise.all([
        stationsService.list(),
        sitesService.list(),
        locationsService.list()
      ]);
      
      // Hesap Yöneticisi için sadece kendi sitesinin istasyonlarını göster
      if (isEffectiveHesapYoneticisi && effectiveSiteId) {
        stationsData = (stationsData || []).filter(s => s.account_id === effectiveSiteId || s.site_id === effectiveSiteId);
        sitesData = (sitesData || []).filter(s => s.id === effectiveSiteId);
        locationsData = (locationsData || []).filter(l => l.site_id === effectiveSiteId);
      }
      
      setStations(stationsData || []);
      setSites(sitesData || []);
      setLocations(locationsData || []);
      setError(null);
    } catch (err) {
      // Hata olsa bile mevcut verileri koru, sadece boşsa hata göster
      if (stations.length === 0) {
        setError(getErrorMessage(err));
      }
      console.error('Stations fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [stations.length]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds for live data
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const openCreateModal = () => {
    setForm({
      name: '',
      site_id: canSelectSite ? '' : effectiveSiteId,
      location_id: '',
      ocpp_id: '',
      qr_code: '',
      serial_number: '',
      connector_type: 'Type 2',
      power_capacity: 22,
      status: 'available'
    });
    setModal({
      isOpen: true,
      mode: 'create',
      stationId: null,
      saving: false
    });
  };

  const openEditModal = (station) => {
    setForm({
      name: station.name || '',
      site_id: station.site_id || '',
      location_id: station.location_id || '',
      ocpp_id: station.ocpp_id || '',
      qr_code: station.qr_code || '',
      serial_number: station.serial_number || '',
      connector_type: station.connector_type || 'Type 2',
      power_capacity: station.power_capacity || 22,
      status: station.status || 'available'
    });
    setModal({
      isOpen: true,
      mode: 'edit',
      stationId: station.id,
      saving: false
    });
  };

  const closeModal = () => {
    if (modal.saving) return;
    setModal(prev => ({ ...prev, isOpen: false }));
  };

  const openDeleteModal = (station) => {
    setDeleteModal({
      isOpen: true,
      station: station,
      deleting: false
    });
  };

  const closeDeleteModal = () => {
    if (deleteModal.deleting) return;
    setDeleteModal({ isOpen: false, station: null, deleting: false });
  };

  // Remote control functions
  const openControlModal = (station, action) => {
    setControlModal({
      isOpen: true,
      station: station,
      action: action,
      executing: false
    });
  };

  const closeControlModal = () => {
    if (controlModal.executing) return;
    setControlModal({ isOpen: false, station: null, action: null, executing: false });
  };

  const executeRemoteControl = async () => {
    if (!controlModal.station || !controlModal.action || controlModal.executing) return;
    
    setControlModal(prev => ({ ...prev, executing: true }));
    
    try {
      const ocppId = controlModal.station.ocpp_id;
      
      switch (controlModal.action) {
        case 'start':
          await ocppLogsService.remoteStart(ocppId, 'DEFAULT', 1);
          toast.success('Şarj başlatma komutu gönderildi');
          break;
        case 'stop':
          // Get transaction ID from OCPP live status
          const liveStatus = await ocppLogsService.getLiveStatus();
          const chargerStatus = liveStatus?.chargers?.find(c => c.charger_id === ocppId);
          if (chargerStatus?.current_transaction_id) {
            await ocppLogsService.remoteStop(ocppId, chargerStatus.current_transaction_id);
            toast.success('Şarj durdurma komutu gönderildi');
          } else {
            toast.error('Aktif şarj oturumu bulunamadı');
          }
          break;
        case 'reset':
          await ocppLogsService.resetCharger(ocppId, 'Soft');
          toast.success('Reset komutu gönderildi');
          break;
        case 'unlock':
          await ocppLogsService.unlockConnector(ocppId, 1);
          toast.success('Kilit açma komutu gönderildi');
          break;
        default:
          break;
      }
      
      setControlModal({ isOpen: false, station: null, action: null, executing: false });
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
      setControlModal(prev => ({ ...prev, executing: false }));
    }
  };

  // License fee confirmation modal state
  const [licenseFeeModal, setLicenseFeeModal] = useState({
    isOpen: false,
    feeData: null,
    pendingPayload: null
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (modal.saving) return;
    
    // İstasyon Adı, QR Kod veya Seri No'dan en az biri zorunlu
    if (!form.name && !form.qr_code && !form.serial_number) {
      toast.error('İstasyon Adı, QR Kod veya Seri No\'dan en az birini girin');
      return;
    }
    
    // Site bazlı isim benzersizlik kontrolü (sadece isim varsa)
    if (form.name) {
      const siteId = canSelectSite ? form.site_id : effectiveSiteId;
      const existingStation = stations.find(s => 
        s.name?.toLowerCase() === form.name.toLowerCase() && 
        s.site_id === siteId &&
        s.id !== modal.stationId // Düzenleme modunda kendi ismini kontrol etme
      );
      if (existingStation) {
        toast.error('Bu isimde bir istasyon zaten mevcut. Farklı bir isim girin.');
        return;
      }
    }
    
    const payload = {
      name: form.name,
      site_id: canSelectSite ? (form.site_id || null) : effectiveSiteId,
      location_id: form.location_id || null,
      ocpp_id: form.ocpp_id,
      qr_code: form.qr_code || null,
      serial_number: form.serial_number || null,
      connector_type: form.connector_type,
      power_capacity: parseFloat(form.power_capacity) || 22,
      status: form.status
    };

    // Yeni istasyon ekleme ise lisans bedeli kontrolü yap
    if (modal.mode === 'create' && payload.site_id) {
      try {
        const token = localStorage.getItem('hims_token');
        const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(
          `${apiUrl}/api/licenses/calculate-fee/${payload.site_id}`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        const feeData = await response.json();
        
        if (feeData.has_license && !feeData.allowed) {
          toast.error(feeData.message);
          return;
        }
        
        // Lisans varsa ve fiyat artışı olacaksa onay iste
        if (feeData.has_license && feeData.fee_increase > 0) {
          setLicenseFeeModal({
            isOpen: true,
            feeData: feeData,
            pendingPayload: payload
          });
          return;
        }
      } catch (err) {
        console.log('Lisans bedeli hesaplanamadı, devam ediliyor...');
      }
    }
    
    // Normal işlemi devam ettir
    await executeStationSubmit(payload);
  };

  const executeStationSubmit = async (payload) => {
    setModal(prev => ({ ...prev, saving: true }));
    
    try {
      if (modal.mode === 'create') {
        await stationsService.create(payload);
        toast.success('İstasyon başarıyla oluşturuldu');
      } else {
        await stationsService.update(modal.stationId, payload);
        toast.success('İstasyon başarıyla güncellendi');
      }
      
      setModal({ isOpen: false, mode: 'create', stationId: null, saving: false });
      setLicenseFeeModal({ isOpen: false, feeData: null, pendingPayload: null });
      fetchData();
      
    } catch (err) {
      toast.error(getErrorMessage(err));
      setModal(prev => ({ ...prev, saving: false }));
    }
  };

  const handleLicenseFeeConfirm = async () => {
    if (licenseFeeModal.pendingPayload) {
      await executeStationSubmit(licenseFeeModal.pendingPayload);
    }
  };

  const handleLicenseFeeCancel = () => {
    setLicenseFeeModal({ isOpen: false, feeData: null, pendingPayload: null });
  };

  const handleDelete = async () => {
    if (!deleteModal.station || deleteModal.deleting) return;
    
    setDeleteModal(prev => ({ ...prev, deleting: true }));
    
    try {
      await stationsService.delete(deleteModal.station.id);
      toast.success('İstasyon başarıyla silindi');
      setDeleteModal({ isOpen: false, station: null, deleting: false });
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
      setDeleteModal(prev => ({ ...prev, deleting: false }));
    }
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site?.name || '-';
  };

  const getStatusBadge = (station) => {
    // OCPP canlı durumunu öncelikli kullan
    const status = station.ocpp_status || station.status || 'offline';
    const config = STATION_STATUSES[status] || STATION_STATUSES.offline;
    return <NativeBadge variant={config.variant}>{config.label}</NativeBadge>;
  };

  const getOnlineBadge = (station) => {
    if (station.is_online) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#22c55e', fontSize: '12px' }}>
          <Wifi style={{ width: '14px', height: '14px' }} />
          Çevrimiçi
        </span>
      );
    }
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#9ca3af', fontSize: '12px' }}>
        <WifiOff style={{ width: '14px', height: '14px' }} />
        Çevrimdışı
      </span>
    );
  };

  const filteredStations = stations.filter(station => 
    station.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    station.connector_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    station.ocpp_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && stations.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const getControlActionLabel = (action) => {
    switch (action) {
      case 'start': return 'Şarj Başlat';
      case 'stop': return 'Şarj Durdur';
      case 'reset': return 'Cihazı Resetle';
      case 'unlock': return 'Kilidi Aç';
      default: return action;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="stations-page">
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ margin: 0 }}>İstasyonlar</h1>
          <p className="text-slate-500 dark:text-slate-400" style={{ margin: '4px 0 0' }}>Şarj istasyonlarını yönetin ve uzaktan kontrol edin</p>
        </div>
        <NativeButton onClick={openCreateModal} data-testid="create-station-button">
          <Plus style={{ width: '16px', height: '16px' }} />
          Yeni İstasyon
        </NativeButton>
      </div>

      {error && stations.length === 0 && (
        <NativeAlert variant="danger" icon={AlertCircle}>
          {error}
        </NativeAlert>
      )}

      {/* Search & Refresh */}
      <NativeCard>
        <NativeCardContent>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
              <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px' }} className="text-slate-400" />
              <input
                type="text"
                placeholder="İstasyon, OCPP ID veya konektör tipi ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 40px',
                  borderRadius: '8px',
                  border: '1px solid var(--card-border)',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--card-bg)'
                }}
                className="dark:bg-slate-700 dark:border-slate-600 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
            <NativeButton variant="outline" onClick={fetchData} disabled={loading}>
              {loading ? (
                <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />
              ) : (
                <RefreshCw style={{ width: '16px', height: '16px' }} />
              )}
              {loading ? 'Yükleniyor...' : 'Yenile'}
            </NativeButton>
          </div>
        </NativeCardContent>
      </NativeCard>

      {/* Stations Table */}
      <NativeCard>
        <NativeCardHeader>
          <NativeCardTitle icon={Zap}>
            İstasyon Listesi ({filteredStations.length})
          </NativeCardTitle>
        </NativeCardHeader>
        <NativeCardContent style={{ padding: 0 }}>
          <NativeTable>
            <NativeTableHeader>
              <NativeTableHead>İstasyon Adı</NativeTableHead>
              <NativeTableHead>OCPP ID</NativeTableHead>
              <NativeTableHead>Site</NativeTableHead>
              <NativeTableHead>Konektör</NativeTableHead>
              <NativeTableHead>Anlık Güç</NativeTableHead>
              <NativeTableHead>Tüketim</NativeTableHead>
              <NativeTableHead>Bağlantı</NativeTableHead>
              <NativeTableHead>Durum</NativeTableHead>
              <NativeTableHead align="right">İşlemler</NativeTableHead>
            </NativeTableHeader>
            <NativeTableBody>
              {filteredStations.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <EmptyState
                      icon={Zap}
                      title="İstasyon bulunamadı"
                      description="Henüz istasyon eklenmemiş veya arama kriterlerinize uygun istasyon yok"
                      action={
                        <NativeButton onClick={openCreateModal}>
                          <Plus style={{ width: '16px', height: '16px' }} />
                          İlk İstasyonu Oluştur
                        </NativeButton>
                      }
                    />
                  </td>
                </tr>
              ) : (
                filteredStations.map((station) => (
                  <NativeTableRow key={station.id}>
                    <NativeTableCell>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{station.name}</span>
                    </NativeTableCell>
                    <NativeTableCell>
                      <code style={{ fontSize: '12px', backgroundColor: 'var(--card-border)', padding: '2px 6px', borderRadius: '4px' }}>
                        {station.ocpp_id || '-'}
                      </code>
                    </NativeTableCell>
                    <NativeTableCell>
                      <span className="text-gray-900 dark:text-gray-100">{getSiteName(station.site_id)}</span>
                    </NativeTableCell>
                    <NativeTableCell>
                      <span className="text-gray-900 dark:text-gray-100">{station.connector_type}</span>
                    </NativeTableCell>
                    <NativeTableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {(station.current_power || 0) > 0 && (
                          <Activity style={{ width: '14px', height: '14px', color: '#22c55e' }} />
                        )}
                        <span style={{ color: (station.current_power || 0) > 0 ? '#22c55e' : 'var(--text-secondary)', fontWeight: '600' }}>
                          {(station.current_power || 0).toFixed(1)} kW
                        </span>
                      </div>
                      <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                        max: {station.power_capacity} kW
                      </div>
                    </NativeTableCell>
                    <NativeTableCell>
                      {station.active_session_energy !== null && station.active_session_energy !== undefined ? (
                        <span style={{ color: '#8b5cf6', fontWeight: '600' }}>
                          {(station.active_session_energy || 0).toFixed(2)} kWh
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)' }}>-</span>
                      )}
                    </NativeTableCell>
                    <NativeTableCell>
                      {getOnlineBadge(station)}
                    </NativeTableCell>
                    <NativeTableCell>
                      {getStatusBadge(station)}
                    </NativeTableCell>
                    <NativeTableCell align="right">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', flexWrap: 'wrap' }}>
                        {/* Remote control buttons - only for online stations with OCPP ID */}
                        {station.ocpp_id && station.is_online && (
                          <>
                            <NativeButton
                              variant="ghost"
                              size="icon"
                              onClick={() => openControlModal(station, 'start')}
                              title="Şarj Başlat"
                              style={{ color: '#22c55e' }}
                              data-testid={`start-station-${station.id}`}
                            >
                              <Play style={{ width: '16px', height: '16px' }} />
                            </NativeButton>
                            <NativeButton
                              variant="ghost"
                              size="icon"
                              onClick={() => openControlModal(station, 'stop')}
                              title="Şarj Durdur"
                              style={{ color: '#ef4444' }}
                              data-testid={`stop-station-${station.id}`}
                            >
                              <Square style={{ width: '16px', height: '16px' }} />
                            </NativeButton>
                            <NativeButton
                              variant="ghost"
                              size="icon"
                              onClick={() => openControlModal(station, 'reset')}
                              title="Reset"
                              style={{ color: '#f59e0b' }}
                              data-testid={`reset-station-${station.id}`}
                            >
                              <RotateCw style={{ width: '16px', height: '16px' }} />
                            </NativeButton>
                            <NativeButton
                              variant="ghost"
                              size="icon"
                              onClick={() => openControlModal(station, 'unlock')}
                              title="Kilidi Aç"
                              style={{ color: '#3b82f6' }}
                              data-testid={`unlock-station-${station.id}`}
                            >
                              <Unlock style={{ width: '16px', height: '16px' }} />
                            </NativeButton>
                          </>
                        )}
                        <NativeButton
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditModal(station)}
                          data-testid={`edit-station-${station.id}`}
                        >
                          <Pencil style={{ width: '16px', height: '16px' }} />
                        </NativeButton>
                        {isSuperAdmin() && (
                          <NativeButton
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteModal(station)}
                            style={{ color: '#dc2626' }}
                            data-testid={`delete-station-${station.id}`}
                          >
                            <Trash2 style={{ width: '16px', height: '16px' }} />
                          </NativeButton>
                        )}
                      </div>
                    </NativeTableCell>
                  </NativeTableRow>
                ))
              )}
            </NativeTableBody>
          </NativeTable>
        </NativeCardContent>
      </NativeCard>

      {/* Create/Edit Modal */}
      <NativeModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.mode === 'create' ? 'Yeni İstasyon Oluştur' : 'İstasyon Düzenle'}
        disabled={modal.saving}
      >
        <form onSubmit={handleSubmit}>
          {/* Site Seçimi - Sadece Super Admin ve Admin için */}
          {canSelectSite ? (
            <NativeSelect
              label="Site"
              id="site_id"
              value={form.site_id}
              onChange={(e) => updateForm('site_id', e.target.value)}
              placeholder="Site Seçin"
              options={sites.map((site) => ({ value: site.id, label: site.name }))}
              required
            />
          ) : (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }} className="text-foreground">
                Site
              </label>
              <div style={{ padding: '10px 12px', borderRadius: '6px', fontSize: '14px' }} className="bg-muted text-foreground border border-border">
                {sites.find(s => s.id === effectiveSiteId)?.name || 'Site bilgisi yok'}
              </div>
            </div>
          )}

          {/* İstasyon Adı */}
          <NativeInput
            label="İstasyon Adı"
            id="name"
            value={form.name}
            onChange={(e) => updateForm('name', e.target.value)}
            placeholder="Şarj Noktası A1"
          />

          {/* OCPP ID - OCPP bağlantısı için zorunlu */}
          <NativeInput
            label="OCPP ID"
            id="ocpp_id"
            value={form.ocpp_id || ''}
            onChange={(e) => updateForm('ocpp_id', e.target.value)}
            placeholder="HIMS_001"
            required
          />

          {/* İstasyon Adı, QR Kod ve Seri No - En az biri zorunlu */}
          <div style={{ 
            padding: '12px', 
            marginBottom: '16px', 
            borderRadius: '8px', 
            backgroundColor: 'rgba(59, 130, 246, 0.1)', 
            border: '1px solid rgba(59, 130, 246, 0.3)' 
          }}>
            <p style={{ fontSize: '12px', color: '#3b82f6', marginBottom: '12px', fontWeight: '500' }}>
              ℹ️ İstasyon Adı, QR Kod veya Seri No'dan en az birini girin
            </p>
            
            <NativeInput
              label="QR Kod"
              id="qr_code"
              value={form.qr_code || ''}
              onChange={(e) => updateForm('qr_code', e.target.value)}
              placeholder="HIMS-SITE001-CRG001"
            />

            <NativeInput
              label="Seri No"
              id="serial_number"
              value={form.serial_number || ''}
              onChange={(e) => updateForm('serial_number', e.target.value)}
              placeholder="SN-2024-001234"
            />
          </div>

          {/* Teknik Özellikler */}
          <NativeSelect
            label="Konektör Tipi"
            id="connector_type"
            value={form.connector_type}
            onChange={(e) => updateForm('connector_type', e.target.value)}
            options={CONNECTOR_TYPES.map((type) => ({ value: type, label: type }))}
          />

          <NativeInput
            label="Güç Kapasitesi (kW)"
            id="power_capacity"
            type="number"
            min="1"
            step="0.1"
            value={form.power_capacity}
            onChange={(e) => updateForm('power_capacity', e.target.value)}
          />

          <NativeSelect
            label="Durum"
            id="status"
            value={form.status}
            onChange={(e) => updateForm('status', e.target.value)}
            options={Object.entries(STATION_STATUSES)
              .filter(([key]) => !key.includes('Suspended') && !key.includes('Finishing'))
              .map(([value, config]) => ({ value, label: config.label }))}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '8px' }}>
            <NativeButton type="button" variant="outline" onClick={closeModal} disabled={modal.saving}>
              İptal
            </NativeButton>
            <NativeButton type="submit" disabled={modal.saving}>
              {modal.saving ? (
                <>
                  <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />
                  Kaydediliyor...
                </>
              ) : (
                modal.mode === 'create' ? 'Oluştur' : 'Kaydet'
              )}
            </NativeButton>
          </div>
        </form>
      </NativeModal>

      {/* Delete Confirmation Modal */}
      <NativeModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        title="İstasyon Silme Onayı"
        disabled={deleteModal.deleting}
      >
        <div>
          <NativeAlert variant="warning" icon={AlertTriangle}>
            <p style={{ fontWeight: '500', marginBottom: '4px' }}>Dikkat!</p>
            <p style={{ margin: 0, fontSize: '14px' }}>Bu istasyona bağlı aktif şarj oturumu varsa silme işlemi başarısız olacaktır.</p>
          </NativeAlert>
          
          {deleteModal.station && (
            <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', marginTop: '16px' }} className="dark:bg-slate-800">
              <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '14px', marginBottom: '8px' }}>Silinecek istasyon:</p>
              <p className="text-slate-900 dark:text-white" style={{ fontWeight: '500', margin: 0 }}>{deleteModal.station.name}</p>
              <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '14px', marginTop: '4px' }}>
                {deleteModal.station.connector_type} - {deleteModal.station.power_capacity} kW
              </p>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '16px' }}>
            <NativeButton type="button" variant="outline" onClick={closeDeleteModal} disabled={deleteModal.deleting}>
              İptal
            </NativeButton>
            <NativeButton 
              type="button" 
              variant="danger"
              onClick={handleDelete}
              disabled={deleteModal.deleting}
              data-testid="confirm-delete-station"
            >
              {deleteModal.deleting ? (
                <>
                  <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />
                  Siliniyor...
                </>
              ) : (
                'Sil'
              )}
            </NativeButton>
          </div>
        </div>
      </NativeModal>

      {/* Remote Control Confirmation Modal */}
      <NativeModal
        isOpen={controlModal.isOpen}
        onClose={closeControlModal}
        title={`Uzaktan Kontrol - ${getControlActionLabel(controlModal.action)}`}
        disabled={controlModal.executing}
      >
        <div>
          <NativeAlert variant="warning" icon={AlertTriangle}>
            <p style={{ fontWeight: '500', marginBottom: '4px' }}>Onay Gerekli</p>
            <p style={{ margin: 0, fontSize: '14px' }}>
              Bu işlem OCPP protokolü üzerinden cihaza komut gönderecektir.
            </p>
          </NativeAlert>
          
          {controlModal.station && (
            <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', marginTop: '16px' }} className="dark:bg-slate-800">
              <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '14px', marginBottom: '8px' }}>Hedef İstasyon:</p>
              <p className="text-slate-900 dark:text-white" style={{ fontWeight: '500', margin: 0 }}>{controlModal.station.name}</p>
              <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '14px', marginTop: '4px' }}>
                OCPP ID: {controlModal.station.ocpp_id}
              </p>
              <p style={{ fontSize: '14px', marginTop: '12px', color: '#3b82f6', fontWeight: '500' }}>
                İşlem: {getControlActionLabel(controlModal.action)}
              </p>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '16px' }}>
            <NativeButton type="button" variant="outline" onClick={closeControlModal} disabled={controlModal.executing}>
              İptal
            </NativeButton>
            <NativeButton 
              type="button" 
              onClick={executeRemoteControl}
              disabled={controlModal.executing}
              data-testid="confirm-remote-control"
            >
              {controlModal.executing ? (
                <>
                  <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />
                  Gönderiliyor...
                </>
              ) : (
                'Onayla ve Gönder'
              )}
            </NativeButton>
          </div>
        </div>
      </NativeModal>

      {/* License Fee Confirmation Modal */}
      <NativeModal
        isOpen={licenseFeeModal.isOpen}
        onClose={handleLicenseFeeCancel}
        title="Lisans Bedeli Güncellemesi"
      >
        <div>
          <NativeAlert variant="warning" icon={AlertTriangle}>
            <p style={{ fontWeight: '500', marginBottom: '4px' }}>Dikkat!</p>
            <p style={{ margin: 0, fontSize: '14px' }}>
              Bu işlem aylık lisans bedelinizi artıracaktır.
            </p>
          </NativeAlert>
          
          {licenseFeeModal.feeData && (
            <div style={{ padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px', marginTop: '16px', border: '1px solid #bbf7d0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Mevcut Cihaz Sayısı</p>
                  <p style={{ fontWeight: '600', fontSize: '18px', margin: 0 }}>{licenseFeeModal.feeData.current_station_count}</p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Yeni Cihaz Sayısı</p>
                  <p style={{ fontWeight: '600', fontSize: '18px', margin: 0, color: '#059669' }}>{licenseFeeModal.feeData.new_station_count}</p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Mevcut Aylık Bedel</p>
                  <p style={{ fontWeight: '600', fontSize: '18px', margin: 0 }}>
                    {licenseFeeModal.feeData.currency_symbol}{licenseFeeModal.feeData.current_monthly_fee?.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Yeni Aylık Bedel</p>
                  <p style={{ fontWeight: '600', fontSize: '18px', margin: 0, color: '#059669' }}>
                    {licenseFeeModal.feeData.currency_symbol}{licenseFeeModal.feeData.new_monthly_fee?.toFixed(2)}
                  </p>
                </div>
              </div>
              
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '8px' }}>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#92400e' }}>
                  +{licenseFeeModal.feeData.currency_symbol}{licenseFeeModal.feeData.fee_increase?.toFixed(2)} artış
                </p>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#a16207' }}>
                  Birim fiyat: {licenseFeeModal.feeData.currency_symbol}{licenseFeeModal.feeData.unit_price}/cihaz/ay
                </p>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '16px' }}>
            <NativeButton type="button" variant="outline" onClick={handleLicenseFeeCancel}>
              İptal
            </NativeButton>
            <NativeButton 
              type="button" 
              onClick={handleLicenseFeeConfirm}
              data-testid="confirm-license-fee"
            >
              Onayla ve Ekle
            </NativeButton>
          </div>
        </div>
      </NativeModal>
    </div>
  );
}
